/*
 *                     NOTICE
 *            COPYRIGHT 2023 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import * as AbReactResources from "abreact/generated/locale/AbReactResources";
import { AbBooleanLogicTreeFocus } from "abreact/incubator/ui/abBooleanLogicTree/AbBooleanLogicTreeFocus";
import { useRegisterFocusTarget } from "abreact/incubator/ui/abBooleanLogicTree/AbGenericFocusManager";
import { AbBooleanLogicTreeSelectionMachine, ABLOGICTREE_SELECTABLE_CLASS, useRegisterSelectionTarget } from "abreact/incubator/ui/abBooleanLogicTree/model/AbBooleanLogicTreeSelectionMachine";
import { SingleRowActionsMenu } from "abreact/incubator/ui/abBooleanLogicTree/SingleRowActionsMenu";
import { CssUtils } from "abreact/incubator/ui/utils/CssUtils";
import { useOnUnmount } from "abreact/utils/AbHooks";
import { ClassUtils } from "abweb/utils/ClassUtils";
import * as React from "react";
import { useCallback, useMemo, useRef } from "react";
// import RowTarget = AbBooleanLogicTreeFocus.RowTarget;

export function SingleConditionRow(props) {
  const {
    selectionMachine,
    index,
    item
  } = {
    ...props
  };
  const focusTargetRepresentation = useMemo(() => new AbBooleanLogicTreeFocus.RowTarget(props.conditionKey), [props.conditionKey]);
  const elementRef = useRef(null);
  const selectionTargetModel = useRegisterSelectionTarget({
    conditionKey: props.conditionKey
  });
  const managedFocusModel = useRegisterFocusTarget({
    targetRepresentation: focusTargetRepresentation
  });
  useOnUnmount(() => {
    unregisterKeyboardActions(elementRef.current);
  });

  /** User Action Callbacks **/
  const onDeleteClicked = useCallback(() => {
    props.onDeleteClicked(index, item.key);
  }, [props]);
  const onInsertAbove = useCallback(() => {
    props.onInsertAbove(index);
  }, [props]);
  const onInsertBelow = useCallback(() => {
    props.onInsertBelow(index);
  }, [props]);
  const onMoveUp = useCallback(() => {
    props.onMoveUp(index);
  }, [props]);
  const onMoveDown = useCallback(() => {
    props.onMoveDown(index);
  }, [props]);
  const onMakeSubGroupAction = useCallback(() => {
    props.onMakeSubGroupAction(index);
  }, [props]);
  const onDuplicate = useCallback(() => {
    props.onDuplicate(index);
  }, [props]);
  const onGroupWithNext = useCallback(() => {
    props.onGroupWithNext(index);
  }, [props]);
  const onDisable = useCallback(() => {
    props.onDisableSingle(item, index);
  }, [props]);
  const onEnable = useCallback(() => {
    props.onEnableSingle(item, index);
  }, [props]);

  /** HTML Event Callbacks **/
  const onConditionClicked = useCallback(e => {
    if (!props.readonly) selectionMachine?.performSelectionOnConditionClicked(item.key, e);
  }, [props]);
  const onKeyDown = useCallback(e => {
    if (!props.readonly) selectionMachine?.selectionOnKeyDown(e, props.conditionKey);
  }, [props]);
  const onConditionRightClicked = useCallback(e => {
    if (!props.readonly) selectionMachine?.performSelectionOnContextMenu(item.key, e);
  }, [props]);
  const onMouseEnterRowHeader = useCallback(() => {
    props.setDraggableHeaderIndex(index);
  }, [props]);
  const onMouseLeaveRowHeader = useCallback(() => {
    props.setDraggableHeaderIndex(null);
  }, [props]);
  const onDragStart = useCallback(e => {
    if (!(props.readonly || props.nestedUnderDisabled)) props.onRowDragStart(e, props.conditionKey);
  }, [props]);
  const onDragEnd = useCallback(e => {
    props.onRowDragEnd(e, props.conditionKey);
  }, [props]);

  /** Keyboard Callbacks **/
  const registerKeyboardActions = useCallback(ref => {
    unregisterKeyboardActions(elementRef.current);
    if (ref == null) {
      return;
    }
    selectionMachine?.clipboard.registerForCopy(ref, false, AbBooleanLogicTreeSelectionMachine.performCopy(selectionMachine));
    selectionMachine?.clipboard.registerForCut(ref, false, AbBooleanLogicTreeSelectionMachine.performCut(selectionMachine));
    selectionMachine?.clipboard.registerForPaste(ref, false, AbBooleanLogicTreeSelectionMachine.performPaste(selectionMachine));
  }, [selectionMachine, props.conditionKey, elementRef]);
  const unregisterKeyboardActions = useCallback(ref => {
    if (ref == null) {
      return;
    }
    selectionMachine?.clipboard.unregisterForCopy(ref);
    selectionMachine?.clipboard.unregisterForCut(ref);
    selectionMachine?.clipboard.unregisterForPaste(ref);
  }, [selectionMachine, props.conditionKey]);

  /** Hover callbacks **/
  const {
    setHoverRowIndex
  } = props;
  const onMouseOver = useCallback(event => {
    if (ClassUtils.isInstanceOf(event.target, HTMLElement) && event.currentTarget === event.target.closest(".ab-booltree-condition-row")) {
      setHoverRowIndex(index);
    } else {
      setHoverRowIndex(-1);
    }
  }, [index, setHoverRowIndex]);
  const onMouseOut = useCallback(event => {
    setHoverRowIndex(-1);
  }, [setHoverRowIndex]);

  /** State/Props Derived Values **/
  const className = CssUtils.composeClassNames("ab-booltree-condition-row", ABLOGICTREE_SELECTABLE_CLASS, index === 0 && "ab-booltree-is-first-row", !props.nestedUnderDisabled && props.hoverRow && !props.readonly && "ab-booltree-hover-row", selectionTargetModel?.isSelected && !selectionTargetModel?.isFocusedSelection && "selected-condition-single", selectionTargetModel?.isFocusedSelection && "selected-condition-single-in-focus", props.isConditionDisabled && "ab-booltree-condition-row-disabled");
  const selectable = selectionMachine != null;
  const isExternallyDisabled = props.readonly || props.nestedUnderDisabled;
  const isFirstItem = index === 0;
  const isLastItem = index === props.lastItemIndex;

  /** JSX Element Content **/
  const rowHeader = () => {
    if (!isExternallyDisabled) {
      return /*#__PURE__*/React.createElement("div", {
        className: CssUtils.composeClassNames("ab-booltree-row-header", ABLOGICTREE_SELECTABLE_CLASS),
        onMouseEnter: onMouseEnterRowHeader,
        onMouseLeave: onMouseLeaveRowHeader
      }, /*#__PURE__*/React.createElement("div", {
        className: CssUtils.composeClassNames("ab-booltree-drag-affordance", ABLOGICTREE_SELECTABLE_CLASS),
        "data-test-id": props.dataTestIdGenerator?.generateID(props.conditionKey, "drag-affordance")
      }));
    }
    return /*#__PURE__*/React.createElement("div", {
      className: "ab-booltree-row-header-disabled"
    });
  };
  const rowBody = () => {
    const CustomComponent = props.customConditionComponent ?? React.Fragment;
    return /*#__PURE__*/React.createElement(CustomComponent, {
      conditionKey: props.conditionKey,
      compoundConditionRowIndex: props.compoundConditionRowIndex,
      onBeforeUndoableChange: props.onBeforeUndoableChange,
      onAfterUndoableChange: props.onAfterUndoableChange,
      readonly: props.readonly,
      disabled: props.isConditionDisabled || props.nestedUnderDisabled
    });
  };
  const renderConditionMenu = () => {
    return /*#__PURE__*/React.createElement(SingleRowActionsMenu, {
      conditionKey: props.conditionKey,
      isReadonly: props.readonly,
      isConditionDisabled: props.isConditionDisabled,
      onDeleteClicked: onDeleteClicked,
      onInsertAbove: onInsertAbove,
      onInsertBelow: onInsertBelow,
      onMoveUp: onMoveUp,
      onMoveDown: onMoveDown,
      onMakeSubGroupClicked: onMakeSubGroupAction,
      isFirstItem: isFirstItem,
      isLastItem: isLastItem,
      onDuplicate: onDuplicate,
      onGroupWithNext: onGroupWithNext,
      level: props.level,
      dataTestIdGenerator: props.dataTestIdGenerator,
      onDisable: onDisable,
      onEnable: onEnable,
      serverSupportsDisabledConditionSection: props.serverSupportsDisabledConditionSection,
      supportsSubgroups: props.supportsSubgroups,
      rowLanguageSource: props.rowLanguageSource
    });
  };
  const renderConditionEvaluationResult = () => {
    const {
      conditionEvaluationResult
    } = props;
    if (conditionEvaluationResult != null && !props.isConditionDisabled && !props.nestedUnderDisabled) {
      const text = conditionEvaluationResult === true ? AbReactResources.abBooleanLogicTree_true() : conditionEvaluationResult === false ? AbReactResources.abBooleanLogicTree_false() : AbReactResources.abBooleanLogicTree_na();
      const aClass = conditionEvaluationResult === true ? "ab-is-true" : conditionEvaluationResult === false ? "ab-is-false" : "ab-is-na";
      return /*#__PURE__*/React.createElement("div", {
        className: CssUtils.composeClassNames("ab-gexp-t-f-result", aClass),
        "data-test-id": "tf-result"
      }, text);
    }
  };
  const rowAdditionalContent = () => {
    return /*#__PURE__*/React.createElement("div", {
      className: "ab-booltree-single-row-additional-content"
    }, renderConditionMenu(), renderConditionEvaluationResult());
  };
  return /*#__PURE__*/React.createElement("fieldset", {
    ref: ref => {
      if (ref) {
        registerKeyboardActions(ref);
        managedFocusModel.managedFocusRef.assignRef(ref);
        elementRef.current = ref;
      }
    },
    "aria-label": AbReactResources.abBooleanLogicTree_accessibility_singleConditionLabel(props.compoundConditionRowIndex),
    className: className,
    tabIndex: selectable ? 0 : undefined,
    onClick: onConditionClicked,
    onKeyDown: onKeyDown,
    onContextMenu: onConditionRightClicked,
    onMouseOver: onMouseOver,
    onMouseOut: onMouseOut,
    key: props.conditionKey,
    "data-condition-key": props.conditionKey,
    "data-boolean-logic-table-row": String(true),
    "data-test-id": `condition-${props.compoundConditionRowIndex}`,
    draggable: !isExternallyDisabled && props.draggable,
    onDragStart: onDragStart,
    onDragEnd: onDragEnd
  }, rowHeader(), rowBody(), rowAdditionalContent());
}
//# sourceMappingURL=SingleConditionRow.js.map
